import {
  CompTableColumnId,
  CompTableColumns,
  STATIC_COMP_TABLE_COLUMNS,
} from '@aloft/toolkit-shared';

export const COMP_TABLE_COLUMN_SETTINGS_LABELS = {
  address: 'Address',
  similarity: 'Similarity',
  proximity: 'Proximity',
  salePrice: 'Sale Price',
  qualityTier: 'Quality Tier',
  gla: 'GLA',
  quality: 'Quality',
  condition: 'Condition',
  propertyType: 'Property Type',
  yearBuilt: 'Year Built',
  siteSize: 'Site Size',
  saleDate: 'Sale Date',
  cdom: 'CDOM',
  beds: 'Beds',
  fullBaths: 'Full Baths',
  halfBaths: 'Half Baths',
  garage: 'Garage',
  basement: 'Basement',
  pool: 'Pool',
  mlsId: 'MLS ID',
} as const satisfies Partial<Record<CompTableColumnId, string>>;

export type SettableCompTableColumnId = keyof typeof COMP_TABLE_COLUMN_SETTINGS_LABELS;

export const SETTABLE_COMP_TABLE_COLUMN_IDS = Object.keys(
  COMP_TABLE_COLUMN_SETTINGS_LABELS,
) as SettableCompTableColumnId[];

export const PINNED_COLUMN_SETTINGS_IDS = [
  'address',
  'similarity',
] as const satisfies readonly SettableCompTableColumnId[];

const SETTABLE_SET = new Set<CompTableColumnId>(SETTABLE_COMP_TABLE_COLUMN_IDS);
const STATIC_SET = new Set<CompTableColumnId>(STATIC_COMP_TABLE_COLUMNS);
const PINNED_SET = new Set<CompTableColumnId>(PINNED_COLUMN_SETTINGS_IDS);

export type ColumnSettingsItem = {
  id: SettableCompTableColumnId;
  label: string;
  visible: boolean;
  canHide: boolean;
  canReorder: boolean;
};

const isSettableCompTableColumnId = (id: CompTableColumnId): id is SettableCompTableColumnId =>
  SETTABLE_SET.has(id);

const leftoverOrderIds = (columns: CompTableColumns): CompTableColumnId[] =>
  columns.order.filter((id) => !SETTABLE_SET.has(id));

export const getColumnSettingsItems = (columns: CompTableColumns): ColumnSettingsItem[] => {
  const seen = new Set<CompTableColumnId>();
  const ordered: SettableCompTableColumnId[] = [];

  for (const id of PINNED_COLUMN_SETTINGS_IDS) {
    seen.add(id);
    ordered.push(id);
  }

  for (const id of columns.order) {
    if (!isSettableCompTableColumnId(id) || seen.has(id)) continue;
    seen.add(id);
    ordered.push(id);
  }

  for (const id of SETTABLE_COMP_TABLE_COLUMN_IDS) {
    if (seen.has(id)) continue;
    ordered.push(id);
  }

  const hidden = new Set(columns.hidden);
  return ordered.map((id) => ({
    id,
    label: COMP_TABLE_COLUMN_SETTINGS_LABELS[id],
    canHide: !STATIC_SET.has(id),
    canReorder: !PINNED_SET.has(id),
    visible: STATIC_SET.has(id) || !hidden.has(id),
  }));
};

export const reorderColumnSettings = (
  columns: CompTableColumns,
  activeId: CompTableColumnId,
  overId: CompTableColumnId,
): CompTableColumns => {
  if (PINNED_SET.has(activeId) || PINNED_SET.has(overId)) return columns;

  const ids = getColumnSettingsItems(columns).map((item) => item.id);
  const from = ids.indexOf(activeId as SettableCompTableColumnId);
  const to = ids.indexOf(overId as SettableCompTableColumnId);
  if (from === -1 || to === -1 || from === to) return columns;

  const nextIds = [...ids];
  const [moved] = nextIds.splice(from, 1);
  if (!moved) return columns;
  nextIds.splice(to, 0, moved);

  const leftover = leftoverOrderIds(columns);
  return { ...columns, order: [...nextIds, ...leftover] };
};

export const setColumnSettingsVisibility = (
  columns: CompTableColumns,
  id: CompTableColumnId,
  visible: boolean,
): CompTableColumns => {
  if (STATIC_SET.has(id)) return columns;
  const hidden = visible
    ? columns.hidden.filter((hiddenId) => hiddenId !== id)
    : columns.hidden.includes(id)
      ? columns.hidden
      : [...columns.hidden, id];
  return { ...columns, hidden };
};

export const toPersistedCompTableColumns = (columns: CompTableColumns): CompTableColumns => {
  const items = getColumnSettingsItems(columns);
  const leftover = leftoverOrderIds(columns);
  return {
    order: [...items.map((item) => item.id), ...leftover],
    hidden: items.filter((item) => item.canHide && !item.visible).map((item) => item.id),
  };
};
